/**
 * Auto-apply discount codes for featured-product sections (LP).
 *
 * Additionally: remember the applied LP codes and remove them after 60 minutes
 * (TTL) so they don't remain active if someone returns much later.
 */
(function () {
  const STORAGE_KEY = "oo_fp_lp_discount_v1";
  const LP_DISCOUNT_TTL_MS = 60 * 60 * 1000; // 60 minutes

  const sections = new Map();
  let pendingSectionId = null;
  let pendingDiscountCodes = null;
  let pendingRemoveExpiredCodes = null;
  let discountAppliedForSection = null;
  let hooksInstalled = false;
  let submitListenerInstalled = false;

  function normalizeCodes(codes) {
    if (!Array.isArray(codes)) return [];

    const seen = new Set();
    const result = [];

    codes.forEach((value) => {
      const code = (value || "").trim();
      if (!code || seen.has(code)) return;
      seen.add(code);
      result.push(code);
    });

    return result;
  }

  function codesKey(codes) {
    return normalizeCodes(codes).sort().join("\0");
  }

  function clearPending() {
    pendingSectionId = null;
    pendingDiscountCodes = null;
    pendingRemoveExpiredCodes = null;
  }

  function readStoredLpDiscount() {
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (!parsed || !Array.isArray(parsed.codes) || !parsed.appliedAt) return null;

      const appliedAt = Number(parsed.appliedAt);
      if (!Number.isFinite(appliedAt)) return null;

      return { appliedAt, codes: normalizeCodes(parsed.codes) };
    } catch (e) {
      return null;
    }
  }

  function rememberLpDiscount(codes) {
    const list = normalizeCodes(codes);
    if (!list.length) return;

    try {
      sessionStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ appliedAt: Date.now(), codes: list }),
      );
    } catch (e) {
      /* ignore */
    }
  }

  function clearStoredLpDiscount() {
    try {
      sessionStorage.removeItem(STORAGE_KEY);
    } catch (e) {
      /* ignore */
    }
  }

  function getExpiredLpCodes() {
    const stored = readStoredLpDiscount();
    if (!stored?.codes?.length) return null;
    if (Date.now() - stored.appliedAt <= LP_DISCOUNT_TTL_MS) return null;
    return stored.codes;
  }

  async function fetchCart() {
    const root = window.Shopify?.routes?.root ?? "/";
    const response = await fetch(`${root}cart.js`, {
      credentials: "same-origin",
    });
    if (!response.ok) throw new Error(response.status);
    return response.json();
  }

  function extractCartDiscountCodes(cart) {
    if (!cart) return [];

    if (Array.isArray(cart.discount_codes)) {
      return cart.discount_codes
        .map((entry) => {
          if (typeof entry === "string") return (entry || "").trim();
          return (entry.code || entry.title || "").trim();
        })
        .filter(Boolean);
    }

    return (cart.cart_level_discount_applications || [])
      .map((app) => (app.title || "").trim())
      .filter(Boolean);
  }

  async function setCartDiscountCodes(codes) {
    const root = window.Shopify?.routes?.root ?? "/";
    const list = normalizeCodes(codes);

    await fetch(`${root}cart/update.js`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "same-origin",
      body: JSON.stringify({ discount: list.join(",") }),
    });
  }

  function parseCodesFromDataset(value) {
    if (!value) return [];

    try {
      const parsed = JSON.parse(value);
      return normalizeCodes(Array.isArray(parsed) ? parsed : []);
    } catch (e) {
      return [];
    }
  }

  function isActiveCodes(sectionId, codes) {
    if (!sectionId) return false;

    const registered = sections.get(sectionId);
    if (!registered?.length) return false;

    return codesKey(registered) === codesKey(codes);
  }

  async function clearCartDiscount() {
    const root = window.Shopify?.routes?.root ?? "/";

    try {
      await fetch(`${root}cart/update.js`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "same-origin",
        body: JSON.stringify({ discount: "" }),
      });
    } catch (e) {
      /* ignore */
    }
  }

  async function applyDiscounts(codes) {
    const list = normalizeCodes(codes);
    if (!list.length) return;

    await setCartDiscountCodes(list);

    rememberLpDiscount(list);
  }

  async function removeExpiredLpCodes(codesToRemove) {
    const toRemove = normalizeCodes(codesToRemove);
    if (!toRemove.length) return;

    const removeSet = new Set(toRemove.map((c) => c.toUpperCase()));
    const cart = await fetchCart();
    const current = extractCartDiscountCodes(cart);

    const remaining = current.filter(
      (code) => !removeSet.has(String(code).toUpperCase()),
    );

    await setCartDiscountCodes(remaining);
    clearStoredLpDiscount();
  }

  async function fetchCartSections(cart) {
    if (!cart?.getSectionsToRender) return null;

    const root = window.Shopify?.routes?.root ?? "/";
    const sectionIds = cart
      .getSectionsToRender()
      .map((section) => section.id)
      .join(",");

    const response = await fetch(`${root}?sections=${sectionIds}`);
    if (!response.ok) return null;

    return { sections: await response.json() };
  }

  function installSubmitListener() {
    if (submitListenerInstalled) return;
    submitListenerInstalled = true;

    document.addEventListener(
      "submit",
      (event) => {
        const form = event.target.closest?.('form[data-type="add-to-cart-form"]');
        if (!form) return;

        clearPending();

        // Don't interfere with cart upsell discount application.
        if (form.closest(".oo-cart-upsell-item")) return;

        const sectionRoot = form.closest("[data-oo-fp-discount-root]");
        if (!sectionRoot) {
          const expired = getExpiredLpCodes();
          if (expired?.length) pendingRemoveExpiredCodes = expired;
          return;
        }

        const sectionId = sectionRoot.dataset.ooFpDiscountSectionId;
        const codes = parseCodesFromDataset(
          form.closest("product-form")?.dataset.ooAutoDiscountCodes,
        );

        if (!isActiveCodes(sectionId, codes)) return;

        pendingSectionId = sectionId;
        pendingDiscountCodes = codes;
      },
      true,
    );
  }

  function installCartUpdateHook() {
    if (window.__ooFpDiscountCartHook) return;
    if (typeof subscribe !== "function" || typeof PUB_SUB_EVENTS === "undefined") {
      return;
    }

    window.__ooFpDiscountCartHook = true;

    subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event?.source !== "product-form") return;

      const codes = pendingDiscountCodes;
      const sectionId = pendingSectionId;
      const removeCodes = pendingRemoveExpiredCodes;
      clearPending();

      if (removeCodes?.length) {
        (async () => {
          try {
            await removeExpiredLpCodes(removeCodes);
            const cart = document.querySelector("cart-drawer");
            if (typeof cart?.refreshCartDrawer === "function") {
              cart.refreshCartDrawer({});
            }
          } catch (e) {
            console.warn("[oo-featured-product-discount][removeExpired]", e);
          }
        })();
        return;
      }

      if (!isActiveCodes(sectionId, codes)) return;

      discountAppliedForSection = sectionId;

      (async () => {
        try {
          await applyDiscounts(codes);
          const cart = document.querySelector("cart-drawer");
          if (typeof cart?.refreshCartDrawer === "function") {
            cart.refreshCartDrawer({});
          }
        } catch (e) {
          console.warn("[oo-featured-product-discount]", e);
          discountAppliedForSection = null;
        }
      })();
    });
  }

  function installRenderHook() {
    const cart = document.querySelector("cart-drawer");
    if (!cart || cart.__ooFpDiscountRenderHook) return;

    cart.__ooFpDiscountRenderHook = true;
    const originalRender = cart.renderContents.bind(cart);

    cart.renderContents = function (parsedState, isClosedCart = true) {
      const sectionId = discountAppliedForSection;
      const codes = sectionId ? sections.get(sectionId) : null;

      discountAppliedForSection = null;

      if (!sectionId || !codes?.length) {
        return originalRender(parsedState, isClosedCart);
      }

      return (async () => {
        try {
          const refreshed = await fetchCartSections(cart);
          originalRender(refreshed || parsedState, isClosedCart);
        } catch (e) {
          console.warn("[oo-featured-product-discount]", e);
          originalRender(parsedState, isClosedCart);
        }
      })();
    };
  }

  function ensureHooks() {
    if (hooksInstalled) return;
    hooksInstalled = true;
    installSubmitListener();
    installCartUpdateHook();
    installRenderHook();
  }

  function unregister(sectionId) {
    sections.delete(sectionId);
    clearPending();

    if (discountAppliedForSection === sectionId) {
      discountAppliedForSection = null;
    }

    clearCartDiscount();
  }

  window.__ooRegisterFeaturedProductDiscount = function ({
    sectionId,
    discountCode,
    discountCodes,
  }) {
    if (!sectionId) return;

    let codes = discountCodes;

    if (!Array.isArray(codes)) {
      codes = discountCode ? [discountCode] : [];
    }

    codes = normalizeCodes(codes);

    if (!codes.length) {
      unregister(sectionId);
      return;
    }

    sections.set(sectionId, codes);
    ensureHooks();
  };

  // Ensure expiry logic is active even when no LP section is present (e.g. normal PDP).
  ensureHooks();

  document.dispatchEvent(new CustomEvent("oo-fp-discount-ready"));
})();
